import React, { useState, useRef, useEffect } from 'react';
import { PlayIcon, SpeakerXMarkIcon, SpeakerWaveIcon, VideoCameraIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import PropTypes from 'prop-types';

/**
 * IntroVideo Component
 * Displays intro video or text from employer
 * Requires full video playback before allowing to continue
 * Tracks viewing in localStorage and can notify backend
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} props.introType - Type of intro: 'video' or 'text'
 * @param {string} props.introVideoUrl - URL of the intro video (if video type)
 * @param {string} props.introText - Intro text content (if text type)
 * @param {Function} props.onIntroComplete - Callback when intro finishes/is skipped
 * @param {boolean} props.isLoading - Show loading state
 * @param {string} props.sessionToken - Session token for tracking
 * @returns {React.ReactElement}
 */
const IntroVideo = ({ 
  introType, 
  introVideoUrl, 
  introText,
  onIntroComplete,
  isLoading = false,
  sessionToken = ''
}) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [hasCompleted, setHasCompleted] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [showProceedScreen, setShowProceedScreen] = useState(true); // Show welcome screen before video
  const [videoWatchedFully, setVideoWatchedFully] = useState(introType === 'text'); // Text doesn't need watching

  // Storage key for tracking watched videos
  const storageKey = `intro_watched_${sessionToken}`;

  // Check if video was already watched in this session
  useEffect(() => {
    const wasWatched = localStorage.getItem(storageKey);
    if (wasWatched === 'true' && introType === 'video') {
      setVideoWatchedFully(true);
    }
  }, [sessionToken, storageKey, introType]);

  // Auto-start video when ready (only after proceed screen is dismissed)
  useEffect(() => {
    if (videoRef.current && introType === 'video' && !hasCompleted && !showProceedScreen) {
      // Attempt to autoplay
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => setIsPlaying(true))
          .catch((error) => {
            console.log('Autoplay prevented:', error);
            // User must click play
            setIsPlaying(false);
          });
      }
    }
  }, [introType, hasCompleted, showProceedScreen]);

  const handlePlayClick = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => setIsPlaying(true))
            .catch((error) => console.error('Play error:', error));
        }
      }
    }
  };

  const handleVideoEnd = () => {
    setIsPlaying(false);
    setVideoWatchedFully(true);
    
    // Store in localStorage so they don't have to rewatch if they go back
    if (sessionToken) {
      localStorage.setItem(storageKey, 'true');
      localStorage.setItem(`intro_watched_at_${sessionToken}`, new Date().toISOString());
    }
    
    // TODO: Optionally send to backend for compliance tracking
    // trackIntroWatched(sessionToken);
  };

  const handleSkip = () => {
    setHasCompleted(true);
    setTimeout(() => {
      onIntroComplete();
    }, 300);
  };

  const handleProceed = () => {
    setShowProceedScreen(false);
  };

  const handleMuteToggle = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  // If intro is complete, return nothing
  if (!showIntro || hasCompleted) {
    return null;
  }

  // No intro content
  if (!introType || (!introVideoUrl && !introText)) {
    return null;
  }

  // Proceed screen — shown before the video/text to give the candidate a moment
  if (showProceedScreen) {
    const isVideo = introType === 'video' && introVideoUrl;
    return (
      <div className="mb-8 sm:mb-10">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 sm:px-8 py-4 flex items-center space-x-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              {isVideo ? (
                <VideoCameraIcon className="w-5 h-5 text-white" />
              ) : (
                <DocumentTextIcon className="w-5 h-5 text-white" />
              )}
            </div>
            <h3 className="text-white font-semibold text-lg">Message from Employer</h3>
          </div>

          {/* Content */}
          <div className="p-8 sm:p-12 flex flex-col items-center text-center">
            {/* Icon */}
            <div className="w-20 h-20 bg-indigo-50 rounded-full flex items-center justify-center mb-6">
              {isVideo ? (
                <VideoCameraIcon className="w-10 h-10 text-indigo-600" />
              ) : (
                <DocumentTextIcon className="w-10 h-10 text-indigo-600" />
              )}
            </div>

            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              Your Employer Has a Message for You
            </h2>
            <p className="text-gray-600 text-base sm:text-lg max-w-md mb-8">
              {isVideo
                ? 'Before your interview begins, please watch a short video message from your employer.'
                : 'Before your interview begins, please read a message from your employer.'}
            </p>

            {isVideo && (
              <div className="flex items-center space-x-2 text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-8">
                <span>⏱️</span>
                <span>You must watch the full video before continuing to your interview.</span>
              </div>
            )}

            <button
              onClick={handleProceed}
              disabled={isLoading}
              className="inline-flex items-center space-x-3 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white font-semibold px-8 py-4 rounded-xl text-lg transition-colors shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PlayIcon className="w-6 h-6" />
              <span>{isVideo ? 'Watch Introduction' : 'Read Message'}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8 sm:mb-10">
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <PlayIcon className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-white font-semibold text-lg">Message from Employer</h3>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 sm:p-8">
          {introType === 'video' && introVideoUrl ? (
            <div
              className="relative bg-black rounded-xl overflow-hidden group cursor-pointer"
              onClick={!isLoading ? handlePlayClick : undefined}
            >
              {/* Video Element */}
              <video
                ref={videoRef}
                src={introVideoUrl}
                className="w-full h-auto max-h-96 object-cover"
                onEnded={handleVideoEnd}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
              />

              {/* Play/Pause Overlay — always visible on hover, play icon shown when paused */}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                {!isPlaying && (
                  <div className="bg-black/30 absolute inset-0" />
                )}
                <div className="relative w-16 h-16 bg-white/90 rounded-full flex items-center justify-center shadow-lg">
                  {isPlaying ? (
                    /* Pause icon — two bars */
                    <div className="flex items-center justify-center space-x-1">
                      <span className="w-1.5 h-6 bg-indigo-600 rounded-full" />
                      <span className="w-1.5 h-6 bg-indigo-600 rounded-full" />
                    </div>
                  ) : (
                    <PlayIcon className="w-8 h-8 text-indigo-600 ml-1" />
                  )}
                </div>
              </div>

              {/* Video Controls — mute only; play/pause handled by clicking the video */}
              <div
                className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4 flex items-center justify-end opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => e.stopPropagation()} // prevent mute click from toggling play
              >
                <button
                  onClick={handleMuteToggle}
                  disabled={isLoading}
                  className="text-white hover:text-white/80 transition-colors disabled:opacity-50"
                >
                  {isMuted ? (
                    <SpeakerXMarkIcon className="w-5 h-5" />
                  ) : (
                    <SpeakerWaveIcon className="w-5 h-5" />
                  )}
                </button>
              </div>

              {/* Loading indicator */}
              {isLoading && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
                </div>
              )}
            </div>
          ) : introType === 'text' && introText ? (
            <div className="max-w-2xl">
              <div className="prose prose-sm max-w-none">
                <p className="text-gray-700 text-base leading-relaxed whitespace-pre-wrap mb-6">
                  {introText}
                </p>
              </div>
            </div>
          ) : null}

          {/* Info message and Continue button */}
          <div className="mt-6 pt-6 border-t border-gray-200 flex items-center justify-between">
            {introType === 'video' && !videoWatchedFully && (
              <p className="text-sm text-amber-600 font-medium">
                ⏱️ Please watch the complete video to continue
              </p>
            )}
            {videoWatchedFully && introType === 'video' && (
              <p className="text-sm text-green-600 font-medium flex items-center">
                ✓ Video watched
              </p>
            )}
            {introType === 'text' && (
              <p className="text-sm text-gray-600">
                📝 Message from your interviewer
              </p>
            )}
            <button
              onClick={handleSkip}
              disabled={isLoading || !videoWatchedFully}
              className={`px-6 py-2 font-medium rounded-lg transition-colors ${
                videoWatchedFully
                  ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isLoading ? 'Processing...' : 'Continue to Setup'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

IntroVideo.propTypes = {
  introType: PropTypes.oneOf(['video', 'text']),
  introVideoUrl: PropTypes.string,
  introText: PropTypes.string,
  onIntroComplete: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

export default IntroVideo;
